export type PlanTier = 'Lite' | 'Plus' | 'Enterprise';

export interface PlanFeature {
  label: string;
  included: boolean;
}

export interface PlanQuotas {
  docsPerMonth: number | null; // null = no fixed limit
  storageGb: number | null;
}

export interface Plan {
  tier: PlanTier;
  name: string;
  tagline: string;
  priceLabel: string; // display only; real price lives in Stripe
  priceSuffix: string;
  billingNote: string;
  docsLabel: string;
  storageLabel: string;
  quotas: PlanQuotas;
  cta: 'checkout' | 'contact';
  highlighted: boolean;
  featuresIntro?: string;
  features: PlanFeature[];
}

export const PLANS: Plan[] = [
  {
    tier: 'Lite',
    name: 'Lite',
    tagline: 'Get compliant without writing code',
    priceLabel: '€99',
    priceSuffix: '/ month',
    billingNote: 'per VAT number, billed monthly',
    docsLabel: '100 documents / month',
    storageLabel: '1 GB archive storage',
    quotas: { docsPerMonth: 100, storageGb: 1 },
    cta: 'checkout',
    highlighted: false,
    features: [
      { label: 'Unlimited users', included: true },
      { label: 'Legal compliance archiving', included: true },
      { label: 'CSV upload with easy mapping tool', included: true },
      { label: 'Manual invoice & credit note creation', included: true },
      { label: 'Support Knowledge Hub', included: true }
    ]
  },
  {
    tier: 'Plus',
    name: 'Plus',
    tagline: 'Automate with the API and let AI fix errors',
    priceLabel: '€169',
    priceSuffix: '/ month',
    billingNote: 'per VAT number, billed monthly',
    docsLabel: '1,000 documents / month',
    storageLabel: '2 GB archive storage',
    quotas: { docsPerMonth: 1000, storageGb: 2 },
    cta: 'checkout',
    highlighted: true,
    featuresIntro: 'All of Lite, and',
    features: [
      { label: 'Full API access', included: true },
      { label: 'AI error handling', included: true },
      { label: 'Automated daily activity reports', included: true }
    ]
  },
  {
    tier: 'Enterprise',
    name: 'Enterprise',
    tagline: 'Many entities, one contract',
    priceLabel: 'Custom',
    priceSuffix: '',
    billingNote: 'volume pricing across all your entities',
    docsLabel: 'Unlimited documents',
    storageLabel: 'Storage sized to your archive',
    quotas: { docsPerMonth: null, storageGb: null },
    cta: 'contact',
    highlighted: false,
    featuresIntro: 'All of Plus, and',
    features: [
      { label: 'Multi-entity management', included: true },
      { label: 'Single sign-on (SSO)', included: true },
      { label: 'Access to tax experts', included: true },
      { label: 'Real-time input language translation', included: true }
    ]
  }
];

// Free tier is not on the pricing cards: 5 docs / month, 200 MB storage
const FREE_QUOTAS: PlanQuotas = { docsPerMonth: 5, storageGb: 200 / 1024 };

// Usage quotas for an org's plan value; Enterprise (Plus) has no fixed limit
export const getPlanQuotas = (plan?: string | null): PlanQuotas => {
  const tier = plan === 'Enterprise Plus' ? 'Enterprise' : plan;
  if (!tier || tier === 'Free') return FREE_QUOTAS;
  return PLANS.find((p) => p.tier === tier)?.quotas ?? { docsPerMonth: null, storageGb: null };
};
